import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { CarouselRef } from 'antd/es/carousel';
import { useI18n } from '../../hooks/useI18n';
import catervaImage from '../../assets/CATERVA_OD.jpg';
import sanMartinESImage from '../../assets/SanMartin_ES.webp';
import sanMartinENGImage from '../../assets/SanMartin_ENG.webp';
import { GameSlideProps, GameSlideItem, CarouselArrowProps } from './GameSlide.types';
import {
  GAMES_DATA,
  STEAM_LOGO_BASIC,
  STEAM_LOGO_HOVER,
  GAMESLIDE_CONFIG,
  GAMESLIDE_ACCESSIBILITY,
  GAMESLIDE_CLASSES,
} from './GameSlide.constants';
import {
  GameSlideContainer,
  StyledCarousel,
  GameSlideItem as SlideItem,
  GameImageWrapper,
  GameImage,
  GameContent,
  GameTitle,
  SteamButton,
  SteamIcon,
  SlideAccessibilityLabel,
} from './GameSlide.styles';
import { GameContentSection } from './content/GameContent';

const GameSlide: React.FC<GameSlideProps> = ({
  className,
  style,
  autoplay = false,
  autoplaySpeed = GAMESLIDE_CONFIG.AUTOPLAY_SPEED,
}) => {
  const { t, language } = useI18n();
  const carouselRef = useRef<CarouselRef>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const gameImages = useMemo(
    () => ({
      caterva: catervaImage,
      sanmartin: language === 'es' ? sanMartinESImage : sanMartinENGImage,
    }),
    [language],
  );

  const slideData: GameSlideItem[] = useMemo(
    () =>
      GAMES_DATA.map((game) => ({
        game,
        title: t(game.titleKey),
        buttonText: t(game.buttonTextKey),
        imageSrc: gameImages[game.id as keyof typeof gameImages],
      })),
    [t, gameImages],
  );

  const handleSteamClick = useCallback((steamUrl: string) => {
    return () => {
      window.open(steamUrl, '_blank', 'noopener,noreferrer');
    };
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent, steamUrl: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      window.open(steamUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handleKeyboardNavigation = useCallback((event: KeyboardEvent) => {
    if (!carouselRef.current) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      carouselRef.current.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      carouselRef.current.next();
    }
  }, []);

  useEffect(() => {
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;

    if (isDesktop) {
      document.addEventListener('keydown', handleKeyboardNavigation);

      return () => {
        document.removeEventListener('keydown', handleKeyboardNavigation);
      };
    }
  }, [handleKeyboardNavigation]);

  const CustomPrevArrow = useCallback(
    (props: CarouselArrowProps) => {
      const { onClick } = props;

      const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      };

      return (
        <button
          type="button"
          className="ant-carousel slick-arrow slick-prev"
          onClick={onClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          aria-label={t('games.carousel.previousSlide')}
        >
          &lt;
        </button>
      );
    },
    [t],
  );

  const CustomNextArrow = useCallback(
    (props: CarouselArrowProps) => {
      const { onClick } = props;

      const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick?.();
        }
      };

      return (
        <button
          type="button"
          className="ant-carousel slick-arrow slick-next"
          onClick={onClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          aria-label={t('games.carousel.nextSlide')}
        >
          &gt;
        </button>
      );
    },
    [t],
  );

  const carouselSettings = useMemo(
    () => ({
      dots: GAMESLIDE_CONFIG.DOTS_VISIBLE,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay,
      autoplaySpeed,
      fade: GAMESLIDE_CONFIG.FADE_EFFECT,
      arrows: GAMESLIDE_CONFIG.ARROWS_VISIBLE,
      prevArrow: <CustomPrevArrow />,
      nextArrow: <CustomNextArrow />,
      swipeToSlide: GAMESLIDE_CONFIG.SWIPE_ENABLED,
      accessibility: true,
      pauseOnHover: true,
      pauseOnFocus: true,
      afterChange: (current: number) => setCurrentSlide(current),
    }),
    [autoplay, autoplaySpeed, CustomPrevArrow, CustomNextArrow],
  );

  return (
    <GameSlideContainer
      className={`${GAMESLIDE_CLASSES.CONTAINER} ${className || ''}`}
      style={style}
    >
      <StyledCarousel ref={carouselRef} {...carouselSettings} $dotCount={slideData.length}>
        {slideData.map((slide, index) => (
          <SlideItem
            key={slide.game.id}
            className={GAMESLIDE_CLASSES.SLIDE}
            role={GAMESLIDE_ACCESSIBILITY.GAME_IMAGE_ROLE}
            aria-label={`${GAMESLIDE_ACCESSIBILITY.SLIDE_LABEL} ${index + 1}: ${slide.title}`}
            aria-hidden={index !== currentSlide}
          >
            <SlideAccessibilityLabel aria-hidden="true">
              {t('games.slide.description', {
                gameName: slide.title,
                slideNumber: (index + 1).toString(),
                totalSlides: slideData.length.toString(),
              })}
            </SlideAccessibilityLabel>

            <GameImageWrapper className={GAMESLIDE_CLASSES.IMAGE_WRAPPER}>
              <GameImage
                src={slide.imageSrc}
                alt={t('games.imageAlt', { gameName: slide.title })}
                className={GAMESLIDE_CLASSES.IMAGE}
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </GameImageWrapper>

            <GameContent className={GAMESLIDE_CLASSES.CONTENT}>
              <GameTitle>{slide.title}</GameTitle>

              <SteamButton
                className={GAMESLIDE_CLASSES.BUTTON}
                onClick={handleSteamClick(slide.game.steamUrl)}
                onKeyDown={(event: React.KeyboardEvent) =>
                  handleKeyDown(event, slide.game.steamUrl)
                }
                role={GAMESLIDE_ACCESSIBILITY.STEAM_BUTTON_ROLE}
                tabIndex={index === currentSlide ? 0 : -1}
                aria-label={t('games.steamButton.ariaLabel', {
                  gameName: slide.title,
                  buttonText: slide.buttonText,
                })}
              >
                <SteamIcon aria-hidden="true">
                  <img src={STEAM_LOGO_BASIC} alt="" className="logo-basic" />
                  <img src={STEAM_LOGO_HOVER} alt="" className="logo-hover" />
                </SteamIcon>
                {slide.buttonText}
              </SteamButton>
            </GameContent>
          </SlideItem>
        ))}
      </StyledCarousel>

      <GameContentSection
        gameId={slideData[currentSlide]?.game.id || ''}
        language={language as 'en' | 'es'}
      />
    </GameSlideContainer>
  );
};

export default GameSlide;
